/**
 * useExportExecution — handles the actual export API calls and progress tracking.
 *
 * Manages:
 * - Calling Jira/Linear export APIs
 * - Progress tracking (completed/total/current)
 * - Results collection
 * - Error handling
 */
import { useState, useCallback } from 'react';
import { apiErrorCode } from '../runtime';
import { toUserFacingMessage } from '../../../userFacingError.js';
import { isPartialExportResult } from '../../../index.js';
export function useExportExecution(runtime, { provider, issues, setExportState, jiraConfig, linearConfig, asanaConfig, notionConfig, trelloConfig, clickupConfig, basecampConfig, mondayConfig, githubConfig, metadata, onExportComplete, onReconnectNeeded, prepareIssues, }) {
    const { api: integrationsApi, debug } = runtime;
    const trackFromContentScript = runtime.track;
    const [progress, setProgress] = useState({ completed: 0, total: 0, current: '' });
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const [preparation, setPreparation] = useState(null);
    const handleExport = useCallback(async () => {
        debug.log('Starting export');
        setExportState('exporting');
        setProgress({ completed: 0, total: issues.length, current: issues[0]?.title || '' });
        let phase = prepareIssues ? 'preparation' : 'export';
        try {
            const exportIssues = prepareIssues ? await prepareIssues() : issues;
            setPreparation({ requested: issues.length, prepared: exportIssues.length });
            if (!exportIssues.length)
                throw new Error('No new issues to export. Export already-filed findings from the issue list.');
            phase = 'export';
            let exportResults;
            if (provider === 'jira') {
                const resource = jiraConfig.jiraResources.find(r => r.id === jiraConfig.selectedCloudId);
                const response = await integrationsApi.exportToJira({
                    ticketIds: exportIssues.map(t => t.id),
                    cloudId: jiraConfig.selectedCloudId,
                    projectKey: jiraConfig.selectedProjectKey,
                    issueTypeId: jiraConfig.selectedIssueTypeId,
                    siteUrl: resource?.url,
                    ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
                });
                exportResults = response.results;
            }
            else if (provider === 'linear') {
                const response = await integrationsApi.exportToLinear({
                    ticketIds: exportIssues.map(t => t.id),
                    teamId: linearConfig.selectedTeamId,
                    projectId: linearConfig.selectedProjectId || undefined,
                    ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
                });
                exportResults = response.results;
            }
            else if (provider === 'asana') {
                const response = await integrationsApi.exportToAsana({
                    ticketIds: exportIssues.map(t => t.id),
                    projectGid: asanaConfig.selectedProjectGid,
                    ...(asanaConfig.selectedWorkspaceGid ? { workspaceGid: asanaConfig.selectedWorkspaceGid } : {}),
                    ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
                });
                exportResults = response.results;
            }
            else if (provider === 'github') {
                if (!githubConfig?.installationId || !githubConfig.repositoryId)
                    throw new Error('Choose a GitHub repository before exporting.');
                exportResults = (await integrationsApi.exportToGitHub({ ticketIds: exportIssues.map(t => t.id), ...githubConfig })).results;
            }
            else if (provider === 'clickup') {
                if (!clickupConfig?.workspaceId || !clickupConfig.spaceId || !clickupConfig.listId)
                    throw new Error('Choose a ClickUp list before exporting.');
                exportResults = (await integrationsApi.exportToClickUp({ ticketIds: exportIssues.map(t => t.id), ...clickupConfig })).results;
            }
            else if (provider === 'basecamp') {
                if (!basecampConfig?.accountId || !basecampConfig.projectId || !basecampConfig.todolistId)
                    throw new Error('Choose a Basecamp to-do list before exporting.');
                exportResults = (await integrationsApi.exportToBasecamp({ ticketIds: exportIssues.map(t => t.id), ...basecampConfig })).results;
            }
            else if (provider === 'monday') {
                if (!mondayConfig?.boardId || !mondayConfig.groupId)
                    throw new Error('Choose a monday.com group before exporting.');
                exportResults = (await integrationsApi.exportToMonday({ ticketIds: exportIssues.map(t => t.id), ...mondayConfig })).results;
            }
            else if (provider === 'trello') {
                const response = await integrationsApi.exportToTrello({
                    ticketIds: exportIssues.map(t => t.id),
                    boardId: trelloConfig?.selectedBoardId ?? '',
                    listId: trelloConfig?.selectedListId ?? '',
                    ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
                });
                exportResults = response.results;
            }
            else {
                const response = await integrationsApi.exportToNotion({
                    ticketIds: exportIssues.map(t => t.id),
                    ...(notionConfig.databaseId ? { databaseId: notionConfig.databaseId } : {}),
                    ...(metadata && Object.keys(metadata).length > 0 ? { metadata } : {}),
                });
                exportResults = response.results;
            }
            debug.log('Export completed:', exportResults);
            setResults(exportResults);
            setProgress({ completed: issues.length, total: issues.length, current: '' });
            const successCount = exportResults.filter(r => r.success && !r.pending).length;
            if (successCount > 0) {
                // issue_exported is the funnel's SUCCESS canary (the data-invariants
                // instrumented-never-fired check watches it), so it fires only when at
                // least one issue actually landed, mirroring the dashboard emitter
                // (useBulkActions.ts exportSummary). Until 2026-07-30 this fired
                // unconditionally, so an export where every result failed still
                // emitted the canary with successCount 0.
                trackFromContentScript('issue_exported', {
                    provider,
                    totalIssues: runtime.outcomeAnalytics?.successDenominator === 'processed' ? exportResults.length : exportIssues.length,
                    successCount,
                });
                // Activation funnel step 5 (OVE-2): a successful export. Same success
                // gate as issue_exported so both count the same moment. surface-tagged.
                runtime.trackExportUsed?.({ provider, issue_count: exportIssues.length, success_count: successCount });
            }
            else if (runtime.outcomeAnalytics?.reportPendingOnlyFailure || exportResults.every(r => !r.pending)) {
                // Resolved-but-all-failed stays observable: without this, a broken
                // export is telemetrically identical to "nobody exports".
                trackFromContentScript('export_failed', {
                    provider,
                    reason: 'all_results_failed',
                });
            }
            // A partial (r.success === true but its screenshot did NOT attach) must
            // not sit on the pure-success screen -- that is the silent green
            // OVE-74 exists to kill. Three-way routing, matching the dashboard
            // (useBulkActions.ts exportSummary): any hard failure -> 'error'; else
            // any partial -> 'partial' (warning); else 'success'. The 'error' and
            // 'partial' states both surface the partial rows via ResultsList, so a
            // mixed batch still shows which lost a shot.
            //
            // isPartialExportResult (not a bare `r.partial` check) is load-bearing:
            // it also catches `assetsAttached === false` with the `partial` flag
            // itself unset, the exact drift a hand-rolled `some(r => r.partial)`
            // missed (OVE-84).
            const hasHardFailure = exportResults.some(r => !r.success && !r.pending);
            const hasPartial = exportResults.some(r => r.pending || isPartialExportResult(r));
            const nextState = hasHardFailure
                ? 'error'
                : hasPartial
                    ? 'partial'
                    : 'success';
            debug.log('Export outcome state:', nextState, { hasHardFailure, hasPartial });
            setExportState(nextState);
            onExportComplete?.(exportResults, { requested: issues.length, prepared: exportIssues.length });
        }
        catch (err) {
            if (runtime.reportExecutionError)
                runtime.reportExecutionError(err, { provider, phase });
            else
                debug.error('Export error:', err);
            // Telemetry keeps the RAW reason (that is the debugging signal); only
            // the RENDERED copy is laundered. Raw "Failed to fetch" / "Unexpected
            // token" reached real users via setError until the 2026-07-27 sweep
            // (error-path EP-181/EP-185, the useExportOAuth.loadError sibling).
            const reason = err instanceof Error ? err.message : 'Export failed';
            // export_failed — the capture→export moat endpoint had no failure
            // telemetry (2026-06-11 audit): a broken export looked identical to
            // "nobody exports".
            if (runtime.outcomeAnalytics?.reportThrownFailure !== false) {
                trackFromContentScript('export_failed', { provider, reason });
            }
            // The stored token was rejected mid-export. The modal already has a
            // Reconnect button in its error state, but only the destination-LOADING
            // path ever set the flag, so an export-time expiry dead-ended (EP-096).
            if (apiErrorCode(err) === 'NEEDS_RECONNECT') {
                onReconnectNeeded?.(true);
            }
            setError(toUserFacingMessage(err, 'Export failed'));
            setExportState('error');
        }
    }, [provider, issues, setExportState, jiraConfig, linearConfig, asanaConfig, notionConfig, trelloConfig, clickupConfig, basecampConfig, mondayConfig, githubConfig, metadata, onExportComplete, onReconnectNeeded, prepareIssues, runtime]);
    const resetExecution = useCallback(() => {
        debug.log('Resetting execution state');
        setProgress({ completed: 0, total: 0, current: '' });
        setResults([]);
        setPreparation(null);
        setError(null);
    }, [runtime]);
    return {
        progress,
        results,
        preparation,
        error,
        setError,
        handleExport,
        resetExecution,
    };
}
//# sourceMappingURL=useExportExecution.js.map